import { MoodProfile, Project, ScoredProject } from './types'

export const MOOD_PROFILES: Record<string, MoodProfile> = {
  productive: {
    name: 'productive',
    label: 'Productive',
    importanceWeight: 0.4,
    urgencyWeight: 0.4,
    enjoymentWeight: 0.2
  },
  creative: {
    name: 'creative',
    label: 'Creative',
    importanceWeight: 0.2,
    urgencyWeight: 0.1,
    enjoymentWeight: 0.7
  },
  balanced: {
    name: 'balanced',
    label: 'Balanced',
    importanceWeight: 0.25,
    urgencyWeight: 0.25,
    enjoymentWeight: 0.5
  },
  quickWins: {
    name: 'quickWins',
    label: 'Quick Wins',
    importanceWeight: 0.3,
    urgencyWeight: 0.3,
    enjoymentWeight: 0.4
  }
}

/**
 * Normalize each scoring dimension to a common 0–1 scale before weighting.
 *
 * The raw dimensions live on different ranges (importance 0–3, urgency 1–3,
 * scope 1–3, enjoyment −1–5), so weighting the raw values let enjoyment
 * dominate numerically and made the mood weights misleading. Min–max scaling
 * maps each dimension's lowest option to 0 and highest to 1, so a mood's
 * weights (which sum to 1) finally mean what they say and the final score is a
 * clean 0–1 (rendered as a 0–100 percentage in the UI).
 *
 * "Very Negative" enjoyment floors at 0 — it contributes nothing on the
 * enjoyment axis but does not actively penalize the project (a deliberate
 * product choice; ordering still ranks disliked tasks below neutral ones).
 */
function normalize(value: number, min: number, max: number): number {
  return (value - min) / (max - min)
}

/**
 * Time-decay urgency — the product thesis, frozen 2026-08-03 for the 21-day
 * N=1 window (docs/2026-08-03-decay-thesis-preregistration.md). Do NOT tune
 * these during the window; tuning mid-experiment voids the result.
 *
 * "If you don't work on it, it should become more urgent." Neglect ramps
 * linearly to full over 60 days — long enough that a project skipped for a
 * week isn't screaming, short enough to move inside a 3-week window.
 */
export const DECAY_FULL_NEGLECT_DAYS = 60

/**
 * Ceiling on the urgency bonus, in normalized (0–1) urgency units. At 1.0 a
 * fully-neglected, maximally-important project reaches the top of the urgency
 * axis regardless of the urgency the user typed — which is the point. It can
 * saturate that axis but never exceed it, so the weighted score stays in 0–1
 * and decay can never outweigh the mood's other dimensions.
 */
export const DECAY_MAX_URGENCY_BONUS = 1

const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * How neglected a project is: 0 (worked today) → 1 (fully decayed).
 *
 * Falls back to `createdAt` when `lastWorkedAt` is absent, so a project added
 * five minutes ago doesn't arrive pre-neglected. Clamped at 0 so clock skew or
 * a future-dated seed can't produce a negative bonus.
 */
export function daysSinceWorked(project: Project, now: Date = new Date()): number {
  const clock = project.lastWorkedAt ?? project.createdAt
  if (!clock) return 0
  const days = (now.getTime() - clock.getTime()) / MS_PER_DAY
  return Number.isFinite(days) && days > 0 ? days : 0
}

function neglectFraction(project: Project, now: Date): number {
  return Math.min(daysSinceWorked(project, now) / DECAY_FULL_NEGLECT_DAYS, 1)
}

export function calculateMoodScore(
  project: Project,
  mood: MoodProfile,
  now: Date = new Date(),
): number {
  const importance = normalize(project.importance, 0, 3)
  const scope = normalize(project.scope, 1, 3)
  const enjoyment = normalize(project.enjoyment, -1, 5)

  // Neglect raises urgency, scaled by importance: an important thing you keep
  // skipping climbs, while neglected trivia stays put. Without the importance
  // factor, the app would reward ignoring unimportant work.
  const decayBonus = DECAY_MAX_URGENCY_BONUS * importance * neglectFraction(project, now)
  const urgency = Math.min(1, normalize(project.urgency, 1, 3) + decayBonus)

  return (
    importance * mood.importanceWeight +
    urgency * mood.urgencyWeight +
    enjoyment * mood.enjoymentWeight +
    scope * (mood.scopeWeight || 0)
  )
}

export function scoreProjects(
  projects: Project[],
  moodProfile: MoodProfile,
  now: Date = new Date(),
): ScoredProject[] {
  return projects
    .filter(p => p.status === 'Current' || p.status === 'Future')
    .map(project => ({
      ...project,
      score: calculateMoodScore(project, moodProfile, now)
    }))
    .sort((a, b) => b.score - a.score)
}